import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ScrollTracker from "./components/Tracking/ScrollTracker";
import { initAnalytics } from "./utils/analytics";

// Public Pages
import PublicLayout from "./layouts/PublicLayout";
import SalesLayout from "./layouts/SalesLayout";
import Home from "./pages/public/Home";
import Rooms from "./pages/public/Rooms";
import Meeting from "./pages/public/Meeting";
import Wedding from "./pages/public/Wedding";
import Facilities from "./pages/public/Facilities";
import Gallery from "./pages/public/Gallery";
import Restaurant from "./pages/public/Restaurant";
import PoolCafe from "./pages/public/PoolCafe";
import TableManner from "./pages/public/TableManner";
import CheckReservation from "./pages/public/CheckReservation";
import SalesGallery from "./pages/public/SalesGallery";
import NewsList from "./pages/public/NewsList";
import NewsDetail from "./pages/public/NewsDetail";
import PaymentInstruction from "./pages/public/PaymentInstruction";
import MenuGallery from "./pages/public/MenuGallery";
import MenuDetail from "./pages/public/MenuDetail";
import PaymentSuccess from "./pages/public/PaymentSuccess";
import FamilyHotelBatu from "./pages/public/FamilyHotelBatu";
import HotelDekatSelecta from "./pages/public/HotelDekatSelecta";
import MountainViewHotelBatu from "./pages/public/MountainViewHotelBatu";
import CorporateGroup from "./pages/public/CorporateGroup";
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Admin Pages
import AdminLayout from "./layouts/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import RoomManagement from "./pages/admin/RoomManagement";
import Reservations from "./pages/admin/Reservations";
import Users from "./pages/admin/Users";
import PromoCodes from "./pages/admin/PromoCodes";
import RatePlans from "./pages/admin/RatePlans";
import Reviews from "./pages/admin/Reviews";
import ContentManagement from "./pages/admin/ContentManagement";
import ActivityLogs from "./pages/admin/ActivityLogs";
import MediaConverter from "./pages/admin/MediaConverter";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import PerformanceReport from "./pages/admin/PerformanceReport";
import EmailTemplates from "./pages/admin/EmailTemplates";
import NewsManagement from "./pages/admin/NewsManagement";
import MenuManagement from "./pages/admin/MenuManagement";
import PermissionGuard from "./components/PermissionGuard";

import "./App.css";

function App() {
  useEffect(() => {
    initAnalytics();
    const staticSEO = document.getElementById("seo-static-content");
    if (staticSEO) {
      staticSEO.remove();
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsTracker />
        <ScrollTracker />
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="rooms" element={<Rooms />} />
            <Route path="meeting" element={<Meeting />} />
            <Route path="wedding" element={<Wedding />} />
            <Route path="facilities" element={<Facilities />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="restaurant" element={<Restaurant />} />
            <Route path="pool-cafe" element={<PoolCafe />} />
            <Route path="table-manner" element={<TableManner />} />
            <Route path="check-reservation" element={<CheckReservation />} />
            <Route path="payment-instruction/:bookingCode" element={<PaymentInstruction />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path="news" element={<NewsList />} />
            <Route path="news/:slug" element={<NewsDetail />} />
            <Route path="menus" element={<MenuGallery />} />
            <Route path="menus/:slug" element={<MenuDetail />} />
            <Route path="family-hotel-batu" element={<FamilyHotelBatu />} />
            <Route path="hotel-dekat-selecta" element={<HotelDekatSelecta />} />
            <Route path="mountain-view-hotel-batu" element={<MountainViewHotelBatu />} />
            <Route path="corporate-gathering-batu" element={<CorporateGroup />} />
            <Route path="corporate-group" element={<CorporateGroup />} />
          </Route>

          {/* Sales Gallery Route */}
          <Route path="/sales" element={<SalesLayout />}>
            <Route index element={<SalesGallery />} />
          </Route>

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Admin Routes - Protected by PermissionGuard */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<PermissionGuard permKey="dashboard"><Dashboard /></PermissionGuard>} />
            <Route path="analytics" element={<PermissionGuard permKey="dashboard"><AnalyticsDashboard /></PermissionGuard>} />
            <Route path="performance-report" element={<PermissionGuard permKey="dashboard"><PerformanceReport /></PermissionGuard>} />
            <Route path="rooms" element={<PermissionGuard permKey="rooms"><RoomManagement /></PermissionGuard>} />
            <Route path="reservations" element={<PermissionGuard permKey="reservations"><Reservations /></PermissionGuard>} />
            <Route path="users" element={<PermissionGuard permKey="users"><Users /></PermissionGuard>} />
            <Route path="promo-codes" element={<PermissionGuard permKey="promo"><PromoCodes /></PermissionGuard>} />
            <Route path="rate-plans" element={<PermissionGuard permKey="rooms"><RatePlans /></PermissionGuard>} />
            <Route path="reviews" element={<PermissionGuard permKey="reviews"><Reviews /></PermissionGuard>} />
            <Route path="content" element={<PermissionGuard permKey="content"><ContentManagement /></PermissionGuard>} />
            <Route path="email-templates" element={<PermissionGuard permKey="content"><EmailTemplates /></PermissionGuard>} />
            <Route path="news" element={<PermissionGuard permKey="content"><NewsManagement /></PermissionGuard>} />
            <Route path="menus" element={<PermissionGuard permKey="content"><MenuManagement /></PermissionGuard>} />
            <Route path="media-converter" element={<PermissionGuard permKey="content"><MediaConverter /></PermissionGuard>} />
            <Route path="logs" element={<PermissionGuard permKey="logs"><ActivityLogs /></PermissionGuard>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
